/**
 * AddConnectorDialog — "Add connector" on the list page (2026-10-21): start
 * from what you have, check it, then add.
 *
 *   * Ready-made — the platform's starters: public APIs
 *     (config/api_connector_templates) and well-known services that sign in
 *     (config/mcp_connector_templates — none ships today; a shared one asks
 *     the manager for its token here). For the organization's managers — group admins and super
 *     admins (item 65B): a starter goes onto the one organization being
 *     managed, never offered to every organization (the 2026-10-19 rule).
 *   * MCP server — paste its address (and a key if it needs one); "Check"
 *     connects and lists its tools without saving anything; the name comes
 *     from the server.
 *   * REST API — paste its OpenAPI document or its address; "Read" lists its
 *     actions; the name and base URL come from the document.
 *
 * How it signs in is asked here in the same three steps as its Sign-in tab
 * (item 80) — who signs in, how the service expects the key, the key — so it
 * is added already signing in the way it should. An MCP server's key is
 * entered before "Check": a shared one is kept, any other only lists the
 * tools. Each person with their own key is saved right after the add; each
 * person signing in to a REST API opens the Sign-in tab, where its sign-in
 * addresses are entered.
 *
 * "Add to list" saves it with every tool / action on; everything else (which
 * ones agents get, the guide) is in the connector's own Settings, which open
 * right after.
 */

import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useLanguage } from '@hooks/useLanguage';
import { showNotification } from '@/utils/showNotification';
import { safeHttpUrl } from '@/utils/safeHttpUrl';
import {
  ConnectorDialog, Field, PrimaryAction, phraseConnectorError, useRiskAcknowledgement,
} from '@modules/chatbots/components/ConnectorDialog';
import type { ReadSpecResult } from '@modules/chatbots/services/apiConnectorService';
import { SignInChoice } from '@modules/chatbots/components/SignInChoice';
import {
  NO_SIGN_IN, apiAuthOf, keyTyped, libraryKeyFields, mcpHeaders, signInComplete, type SignInValue,
} from '@modules/chatbots/utils/signIn';
import {
  connectorLibraryService,
  LIBRARY_DESCRIPTION_MAX,
  type LibraryEntryEditable,
  type LibraryKind,
  type LibraryStarter,
  type McpCheckResult,
} from '../services/connectorLibraryService';
import { ServiceIcon } from './LibraryBits';
import { MoreToggle } from './SharingTexts';

type Source = 'starter' | LibraryKind;

/** Mirrors MAX_NAME_LENGTH (mcp_connector_service.py / api_connector_service.py). */
const MAX_NAME_LENGTH = 80;

interface Props {
  /** The organization it is added to — null until it is chosen in `organizationField`. */
  tenantId: string | null;
  organizationName: string;
  /** "Add it to which organization?" (item 83): shown first, when the page shows more than one. */
  organizationField?: ReactNode;
  descendantCount: number;
  kinds: LibraryKind[];
  /** The ready-made connectors are offered (the page's managers). */
  starters: boolean;
  /** Open already on this way of adding (the empty state's suggestions). */
  start?: Source;
  /** What the list already holds — adding the same server or API again says so. */
  existing?: { name: string; kind: LibraryKind; url?: string | null; base_url?: string | null;
               inherited: boolean; owner_name: string | null }[];
  onClose: () => void;
  /** `finishSignIn`: the Sign-in tab opens on that choice (never its key) to finish it. */
  onAdded: (entry: LibraryEntryEditable, finishSignIn?: SignInValue) => void;
}

export function AddConnectorDialog({ tenantId, organizationName, organizationField, descendantCount, kinds,
  starters: offerStarters, existing = [], start, onClose, onAdded }: Props) {
  const { t } = useLanguage();
  const [subtitleMore, setSubtitleMore] = useState(false);
  const subtitleMoreId = useId();
  const [starterList, setStarterList] = useState<LibraryStarter[] | null>(null);
  const [source, setSource] = useState<Source | null>(start ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // MCP
  const [url, setUrl] = useState('');
  const [checked, setChecked] = useState<McpCheckResult | null>(null);
  // API
  const [specText, setSpecText] = useState('');
  const [specUrl, setSpecUrl] = useState('');
  const [read, setRead] = useState<ReadSpecResult | null>(null);
  const [baseUrl, setBaseUrl] = useState('');
  // Both
  const [signIn, setSignIn] = useState<SignInValue>(NO_SIGN_IN);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // A server or API added by hand: the risk acknowledgement beside Add (item 69).
  const ack = useRiskAcknowledgement({ customAdd: true, switchesOn: false });
  // Ready-made services that sign in: the one whose token step is open, and the token.
  const [tokenFor, setTokenFor] = useState<string | null>(null);
  const [token, setToken] = useState('');

  useEffect(() => {
    if (!offerStarters || kinds.length === 0) { setStarterList([]); return; }
    if (!tenantId) return;
    let live = true;
    setStarterList(null);
    connectorLibraryService.starters(tenantId)
      .then(list => { if (live) setStarterList(list); })
      .catch(() => { if (live) setStarterList([]); });
    return () => { live = false; };
  }, [offerStarters, kinds, tenantId]);

  // Offered while the starters load too, so the choices do not jump from two to three;
  // dropped only once the list is known to be empty.
  const choices = useMemo(() => [
    ...(offerStarters && kinds.length > 0 && (starterList === null || starterList.length > 0) ? [{ key: 'starter' as const, heading: t('connectorLibrary.add.starter'), hint: t('connectorLibrary.add.starterHint') }] : []),
    ...(kinds.includes('mcp') ? [{ key: 'mcp' as const, heading: t('connectorLibrary.add.mcp'), hint: t('connectorLibrary.add.mcpHint') }] : []),
    ...(kinds.includes('api') ? [{ key: 'api' as const, heading: t('connectorLibrary.add.api'), hint: t('connectorLibrary.add.apiHint') }] : []),
  ], [offerStarters, starterList, kinds, t]);

  // One way to add: go straight to it.
  useEffect(() => {
    if (source === null && starterList !== null && choices.length === 1) setSource(choices[0].key);
  }, [choices, source, starterList]);

  // Where it goes; nothing below runs before it is chosen (`run` and the form wait for it).
  const to = tenantId ?? '';
  const run = async (fn: () => Promise<void>, fallbackKey: string) => {
    if (!tenantId) return;
    setBusy(true);
    setError(null);
    try { await fn(); } catch (err) { setError(phraseConnectorError(t, err, fallbackKey)); } finally { setBusy(false); }
  };

  // An MCP server's key: sent with "Check", kept only when shared.
  const headers = mcpHeaders(signIn);
  const changeSignIn = (next: SignInValue) => {
    // What "Check" sent no longer matches what "Add" would send: check again.
    if (source === 'mcp' && JSON.stringify(mcpHeaders(next)) !== JSON.stringify(headers)) setChecked(null);
    setSignIn(next);
  };

  const check = () => run(async () => {
    const result = await connectorLibraryService.checkMcp({ url: url.trim(), headers });
    setChecked(result);
    // An OAuth server (MCP authorization spec) answers no one without a sign-in.
    if (result.sign_in) setSignIn(prev => ({ ...prev, mode: 'per_person_oauth' }));
    else setSignIn(prev => (prev.mode === 'per_person_oauth' ? { ...prev, mode: 'none' } : prev));
    if (!name.trim()) setName((result.server_name || hostOf(url) || '').slice(0, MAX_NAME_LENGTH));
  }, 'bots.connectors.mcp.errors.generic');

  const readDocument = () => run(async () => {
    const result = await connectorLibraryService.readSpec(specText.trim() ? { spec_text: specText } : { spec_url: specUrl.trim() });
    setRead(result);
    if (!baseUrl.trim() && result.base_url) setBaseUrl(result.base_url);
    if (!name.trim() && result.title) setName(result.title.slice(0, MAX_NAME_LENGTH));
  }, 'bots.connectors.api.errors.generic');

  /** The per-person and per-agent key settings the create cannot carry are saved right after it. */
  const finish = async (entry: LibraryEntryEditable, key: Record<string, unknown>) => {
    try {
      const saved = await connectorLibraryService.saveSignIn(entry.entry_id, to, signIn.mode, entry.edit_version, key);
      onAdded(saved.entry);
    } catch (err) {
      // Added, but its sign-in is not saved yet: say so, and open the Sign-in tab on the choice made here.
      showNotification(phraseConnectorError(t, err, 'connectorLibrary.signin.finishThere'), 'error');
      onAdded(entry, withoutKey(signIn));
    }
  };

  const add = () => run(async () => {
    const mode = signIn.mode;
    if (source === 'mcp') {
      const oauth = mode === 'per_person_oauth';
      const entry = await connectorLibraryService.createMcp({
        tenant_id: to, name: name.trim(), description: description.trim(), url: checked?.url ?? url.trim(),
        headers: oauth ? undefined : headers, share_credential: mode === 'shared' && !!headers,
        // A server each person signs in to (item 65A): added that way.
        ...(oauth ? { signin_mode: 'per_person_oauth' } : {}),
        ...ack.payload,
      });
      if (mode === 'per_agent' || mode === 'per_person_key') await finish(entry, libraryKeyFields('mcp', signIn));
      else onAdded(entry);
      return;
    }
    const shared = mode === 'shared';
    const auth = apiAuthOf(signIn, shared && keyTyped(signIn));
    const entry = await connectorLibraryService.createApi({
      tenant_id: to, name: name.trim(), description: description.trim(), base_url: baseUrl.trim(),
      ...(specText.trim() ? { spec_text: specText } : { spec_url: specUrl.trim() }),
      operation_settings: Object.fromEntries((read?.operations ?? []).map(op => [op.operation_id, { enabled: true }])),
      ...auth, share_credential: shared, ...ack.payload,
    });
    if (mode === 'per_person_key') await finish(entry, libraryKeyFields('api', signIn));
    else onAdded(entry, mode === 'per_person_oauth' ? withoutKey(signIn) : undefined);
  }, source === 'mcp' ? 'bots.connectors.mcp.errors.generic' : 'bots.connectors.api.errors.generic');

  const addStarter = (starter: LibraryStarter) => {
    // A service that signs in asks for its token first, inside its own row.
    if (starter.needs_credential && tokenFor !== starter.template_id) {
      setTokenFor(starter.template_id); setToken(''); setError(null);
      return undefined;
    }
    return run(async () => {
      onAdded(await connectorLibraryService.addStarter(starter.template_id, to,
        starter.needs_credential ? token.trim() : undefined));
    }, starter.kind === 'mcp' ? 'bots.connectors.mcp.errors.generic' : 'connectorLibrary.add.starterError');
  };

  const ready = source === 'mcp' ? !!checked : source === 'api' ? !!read : false;
  // The same address already on the list (its own or shared from above).
  const duplicate = ready ? existing.find(e => e.kind === source && sameAddress(
    source === 'mcp' ? e.url : e.base_url, source === 'mcp' ? (checked?.url ?? url) : baseUrl)) : undefined;
  const canAdd = !!tenantId && ready && !busy && !!name.trim() && (source !== 'api' || !!baseUrl.trim())
    && signInComplete('library', signIn, false) && ack.ok;

  const pick = (next: Source) => {
    setSource(next); setError(null); setChecked(null); setRead(null); setName(''); setSignIn(NO_SIGN_IN); ack.reset();
  };

  return (
    <ConnectorDialog
      size="medium"
      title={t('connectorLibrary.add.title')}
      subtitle={!tenantId ? t('connectorLibrary.add.subtitleChoose') : <>
        {descendantCount > 0
          ? t('connectorLibrary.add.subtitleTree', { organization: organizationName, count: descendantCount })
          : t('connectorLibrary.add.subtitle', { organization: organizationName })}{' '}
        {/* Short first; the rest behind "More" (item 80b). */}
        <span id={subtitleMoreId} hidden={!subtitleMore} data-testid="library-add-subtitle-detail">
          {t(descendantCount > 0 ? 'connectorLibrary.add.subtitleMoreTree' : 'connectorLibrary.add.subtitleMore')}{' '}
        </span>
        <MoreToggle open={subtitleMore} onToggle={() => setSubtitleMore(o => !o)} controls={subtitleMoreId}
                    testId="library-add-subtitle-more" />
      </>}
      onClose={onClose}
      note={error}
      noteTone="error"
      testId="library-add-dialog"
      acknowledge={ready ? ack.node : undefined}
      actions={(<>
        <button type="button" className="btn-cancel" onClick={onClose} disabled={busy}>{t('common.cancel')}</button>
        {source !== 'starter' && (
          <PrimaryAction label={t('connectorLibrary.editor.addToList')} busyLabel={t('common.saving')}
                         busy={busy && ready} disabled={!canAdd} onClick={() => { void add(); }} testId="library-add-submit" />
        )}
      </>)}
    >
      <div className="cxl-add">
      {organizationField}
      {tenantId && (<>
      {choices.length > 1 && (
        <Field label={t('connectorLibrary.add.startFrom')}>
          <div className="cxl-choices" role="radiogroup" aria-label={t('connectorLibrary.add.startFrom')}>
            {choices.map(c => (
              <button key={c.key} type="button" role="radio" aria-checked={source === c.key}
                      className={`cxl-choice ${source === c.key ? 'is-on' : ''}`} onClick={() => pick(c.key)}
                      data-testid={`library-add-source-${c.key}`}>
                <span className="cxl-choice__title">{c.heading}</span>
                <span className="cxl-choice__hint">{c.hint}</span>
              </button>
            ))}
          </div>
        </Field>
      )}

      {source === 'starter' && starterList === null && (
        <p className="cxl-starters__loading" role="status">{t('common.loading')}</p>
      )}
      {source === 'starter' && starterList !== null && (
        <ul className="cxl-starters" data-testid="library-starters">
          {starterList.map(s => {
            const asking = tokenFor === s.template_id && !s.on_list;
            return (
            <li key={s.template_id} className={`cxl-starters__row ${asking ? 'is-asking' : ''}`}
                data-testid={`library-starter-${s.template_id}`}>
              <ServiceIcon name={s.name} slug={s.template_id} url={s.url} />
              <span className="cxl-starters__text">
                <span className="cxl-starters__name"><bdi dir="auto">{s.name}</bdi></span>
                <span className="cxl-starters__desc">
                  <bdi dir="auto">{s.description}</bdi>{s.description ? ' · ' : ''}
                  {s.kind === 'mcp'
                    ? t('connectorLibrary.add.readingToolsOn', { count: s.tools_on ?? 0 })
                    : t('connectorLibrary.actionsCount', { count: s.operations ?? 0 })}
                </span>
                {/* True of each kind's ready-made connectors: their pool rules refuse any other. */}
                <span className="cxl-starters__safe" data-testid={`library-starter-safe-${s.template_id}`}>
                  {s.kind !== 'mcp' ? t('connectorLibrary.add.starterSafe')
                    : s.signin_mode === 'per_person_oauth' ? t('connectorLibrary.add.starterSignsInEach')
                      : t('connectorLibrary.add.starterSignsIn')}
                </span>
              </span>
              {s.on_list ? (
                <span className="cx-pill">{t('connectorLibrary.add.onList')}</span>
              ) : !asking && (
                <button type="button" className="cx-btn cx-btn--sm cx-btn--primary" disabled={busy}
                        onClick={() => { void addStarter(s); }} data-testid={`library-starter-add-${s.template_id}`}>
                  {t('connectorLibrary.add.addStarter')}
                </button>
              )}
              {asking && (
                <div className="cxl-starters__token">
                  <label className="form-label" htmlFor={`library-starter-token-${s.template_id}`}>
                    {t('connectorLibrary.add.tokenLabel', { name: s.name })}
                  </label>
                  <p className="form-help-text">
                    {t('connectorLibrary.add.tokenHint')}{' '}
                    {safeHttpUrl(s.token_help_url) && (
                      <a href={safeHttpUrl(s.token_help_url)!} target="_blank" rel="noopener noreferrer">
                        {t('connectorLibrary.add.tokenWhere')}
                      </a>
                    )}
                  </p>
                  <div className="cxd__row">
                    <input id={`library-starter-token-${s.template_id}`} type="password" autoComplete="new-password"
                           className="form-input form-input--mono" dir="ltr" value={token} autoFocus
                           placeholder={s.credential_scheme ? `${s.credential_scheme} …` : ''}
                           onChange={e => setToken(e.target.value)}
                           onKeyDown={e => { if (e.key === 'Enter' && token.trim() && !busy) { e.preventDefault(); void addStarter(s); } }}
                           data-testid={`library-starter-token-${s.template_id}`} />
                    <button type="button" className="btn-cancel" disabled={busy}
                            onClick={() => { setTokenFor(null); setToken(''); }}>
                      {t('common.cancel')}
                    </button>
                    <PrimaryAction label={t('connectorLibrary.editor.addToList')} busyLabel={t('connectorLibrary.add.checking')}
                                   busy={busy} disabled={!token.trim()} onClick={() => { void addStarter(s); }}
                                   testId={`library-starter-connect-${s.template_id}`} />
                  </div>
                </div>
              )}
            </li>
            );
          })}
        </ul>
      )}

      {source === 'mcp' && (
        <>
          <Field label={t('bots.connectors.mcp.urlLabel')} htmlFor="library-add-url">
            <div className="cxd__row">
              <input id="library-add-url" className="form-input form-input--mono" dir="ltr" value={url}
                     onChange={e => { setUrl(e.target.value); setChecked(null); }} placeholder="https://example.com/mcp"
                     onKeyDown={e => { if (e.key === 'Enter' && url.trim() && !busy) { e.preventDefault(); void check(); } }}
                     autoFocus data-testid="library-add-url" />
              <button type="button" className="cx-btn" onClick={() => { void check(); }} disabled={busy || !url.trim()}
                      data-testid="library-add-check">
                {busy && !checked ? t('connectorLibrary.add.checking') : t('connectorLibrary.add.check')}
              </button>
            </div>
          </Field>
          <SignInChoice kind="mcp" scope="library" value={signIn} onChange={changeSignIn} disabled={busy} checkKey
                        name="library-add-auth" />
          {checked?.sign_in && (
            // An OAuth server (MCP authorization spec): no one's tools until
            // a person signs in — it is added with "each person signs in".
            <div className="cxl-checked" role="status" data-testid="library-add-sign-in">
              {t('connectorLibrary.add.signInEach')}
            </div>
          )}
          {checked && !checked.sign_in && (
            <div className="cxl-checked" role="status" data-testid="library-add-checked">
              {t('connectorLibrary.add.connected', { count: checked.tools.length })}
              {checked.tools.length > 0 && (
                <span className="cxl-checked__tools" dir="ltr">
                  {checked.tools.slice(0, 6).map(tool => tool.name).join(', ')}
                  {checked.tools.length > 6 ? '…' : ''}
                </span>
              )}
            </div>
          )}
        </>
      )}

      {source === 'api' && (
        <>
          <Field label={t('bots.connectors.api.specLabel')} hint={t('bots.connectors.api.specHint')} htmlFor="library-add-spec">
            <div className="cxd__stack">
              <textarea id="library-add-spec" className="form-input form-textarea form-input--mono" rows={5} dir="ltr"
                        value={specText} onChange={e => { setSpecText(e.target.value); setRead(null); }}
                        placeholder={'{ "openapi": "3.0.0", ... }'} data-testid="library-add-spec-text" />
              <div className="cxd__row">
                <input className="form-input form-input--mono" dir="ltr" value={specUrl}
                       onChange={e => { setSpecUrl(e.target.value); setRead(null); }}
                       onKeyDown={e => {
                         if (e.key === 'Enter' && (specText.trim() || specUrl.trim()) && !busy) { e.preventDefault(); void readDocument(); }
                       }}
                       placeholder="https://example.com/openapi.json" aria-label={t('bots.connectors.api.specLabel')}
                       data-testid="library-add-spec-url" />
                <button type="button" className="cx-btn" onClick={() => { void readDocument(); }}
                        disabled={busy || (!specText.trim() && !specUrl.trim())} data-testid="library-add-read">
                  {busy && !read ? t('connectorLibrary.add.reading') : t('bots.connectors.api.readSpec')}
                </button>
              </div>
            </div>
          </Field>
          {read && (
            <>
              <div className="cxl-checked" role="status" data-testid="library-add-read-result">
                {t('connectorLibrary.add.readOk', { count: read.operations.length })}
              </div>
              <Field label={t('bots.connectors.api.baseUrlLabel')} htmlFor="library-add-base-url">
                <input id="library-add-base-url" className="form-input form-input--mono" dir="ltr" value={baseUrl}
                       onChange={e => setBaseUrl(e.target.value)} data-testid="library-add-base-url" />
              </Field>
              <SignInChoice kind="api" scope="library" value={signIn} onChange={setSignIn} disabled={busy}
                            name="library-add-auth" />
            </>
          )}
        </>
      )}

      {ready && duplicate && (
        <p className="cxl-add__duplicate" role="status" data-testid="library-add-duplicate">
          {duplicate.inherited
            ? <>{t('connectorLibrary.add.duplicateShared', { owner: duplicate.owner_name ?? '' })} <bdi dir="auto">{duplicate.name}</bdi></>
            : <>{t('connectorLibrary.add.duplicateOwn')} <bdi dir="auto">{duplicate.name}</bdi></>}
        </p>
      )}
      {ready && (
        <>
          <Field label={t('connectorLibrary.add.name')} htmlFor="library-add-name">
            <input id="library-add-name" className="form-input" dir="auto" value={name} maxLength={MAX_NAME_LENGTH}
                   onChange={e => setName(e.target.value)} data-testid="library-add-name" />
          </Field>
          <Field label={t('connectorLibrary.editor.descriptionLabel')} hint={t('connectorLibrary.editor.descriptionHint')}
                 htmlFor="library-add-description">
            <textarea id="library-add-description" className="form-input form-textarea" rows={2} dir="auto"
                      value={description} maxLength={LIBRARY_DESCRIPTION_MAX}
                      onChange={e => setDescription(e.target.value)} data-testid="library-add-description" />
          </Field>
          <p className="cxl-add__after">{t(source === 'api' ? 'connectorLibrary.add.afterApi' : 'connectorLibrary.add.after')}</p>
        </>
      )}
      </>)}
      </div>
    </ConnectorDialog>
  );
}

/** The choice without its key: what may travel to the Sign-in tab. */
function withoutKey(v: SignInValue): SignInValue {
  return { ...v, value: '', username: '', password: '' };
}

/** Two addresses of the same server: scheme, case, `www.` and a trailing slash aside. */
function sameAddress(a: string | null | undefined, b: string | null | undefined): boolean {
  const norm = (v: string | null | undefined) => (v ?? '').trim().toLowerCase()
    .replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '');
  return !!norm(a) && norm(a) === norm(b);
}

function hostOf(value: string): string | null {
  try { return new URL(value.trim()).hostname.replace(/^www\./, ''); } catch { return null; }
}
